import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle, Package, Truck, MapPin, CreditCard } from 'lucide-react';
import { Button } from '../components/ui/button';
import { ordersApi } from '../services/api';
import { useToast } from '../hooks/use-toast';
import SEOHead from '../components/SEOHead';
import { formatPrice } from '../utils/currency';

const OrderConfirmationPage = () => {
  const { orderNumber } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language?.startsWith('es') ? 'es-CO' : 'en-US';
  const { toast } = useToast();
  
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrder();
  }, [orderNumber]);

  const fetchOrder = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        navigate('/');
        return;
      }
      
      const data = await ordersApi.getOrderByNumber(orderNumber, token);
      setOrder(data);
    } catch (error) {
      console.error('Error fetching order:', error);
      toast({
        title: t('common.error'),
        description: t('orderConfirm.failedLoadOrder'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const getEstimatedDelivery = () => {
    const date = new Date();
    date.setDate(date.getDate() + 5); // 5 days from now
    return date.toLocaleDateString(dateLocale, { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#be1f1a] mx-auto mb-4"></div>
          <p className="text-gray-600">{t('checkout.loadingOrderDetails')}</p>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl text-gray-600 mb-4">{t('checkout.orderNotFound')}</p>
          <Link to="/shop">
            <Button className="bg-[#be1f1a] hover:bg-[#a71b17]">
              {t('checkout.continueShopping')}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <SEOHead
        title={order?.orderNumber
          ? `${t('checkoutSuccess.orderConfirmed')} — #${order.orderNumber}`
          : t('checkoutSuccess.orderConfirmed')}
      />
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Success Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-3xl font-serif text-[#2C2420] mb-2">
            {t('orderConfirm.thankYouTitle')}
          </h1>
          <p className="text-gray-600">
            {t('orderConfirm.confirmedShipSoon')}
          </p>
        </div>

        {/* Order Details Card */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="border-b pb-4 mb-4">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm text-gray-500">{t('orderConfirm.orderNumber')}</p>
                <p className="text-xl font-semibold text-[#be1f1a]">{order.orderNumber}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-500">{t('orderConfirm.orderDate')}</p>
                <p className="font-medium">
                  {new Date(order.createdAt).toLocaleDateString(dateLocale, {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </p>
              </div>
            </div>
          </div>

          {/* Estimated Delivery */}
          <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <Truck className="w-6 h-6 text-green-600" />
            <div>
              <p className="font-semibold text-green-900">{t('shipping.estimatedDelivery')}</p>
              <p className="text-sm text-green-700">{getEstimatedDelivery()}</p>
            </div>
          </div>

          {/* Order Items */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Package className="w-5 h-5" />
              {t('orderConfirm.orderItems')}
            </h3>
            <div className="space-y-4">
              {order.items.map((item, index) => (
                <div key={index} className="flex gap-4 pb-4 border-b last:border-b-0">
                  <img
                    src={item.image || 'https://placehold.co/80x100'}
                    alt={item.name.en || item.name}
                    className="w-20 h-24 object-cover rounded"
                  />
                  <div className="flex-1">
                    <p className="font-medium">{item.name.en || item.name}</p>
                    <p className="text-sm text-gray-500">{t('orderConfirm.sku')}: {item.sku}</p>
                    <p className="text-sm text-gray-500">{t('checkoutSuccess.quantity')}: {item.quantity}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-[#be1f1a]">
                      {formatPrice(item.total, order.currency)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Order Summary */}
          <div className="border-t pt-4">
            <div className="space-y-2 mb-4">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">{t('checkout.subtotal')}:</span>
                <span>{formatPrice(order.subtotal, order.currency)}</span>
              </div>
              {order.discount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">{t('checkout.discount')}:</span>
                  <span className="text-green-600">-{formatPrice(order.discount, order.currency)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">{t('orderConfirm.tax')}:</span>
                <span>{formatPrice(order.tax, order.currency)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">{t('checkout.shipping')}:</span>
                <span>{order.shippingCost === 0 ? t('orderConfirm.free') : formatPrice(order.shippingCost, order.currency)}</span>
              </div>
            </div>
            <div className="flex justify-between text-lg font-semibold pt-2 border-t">
              <span>{t('orders.total')}:</span>
              <span className="text-[#be1f1a]">{formatPrice(order.total, order.currency)}</span>
            </div>
          </div>
        </div>

        {/* Shipping & Payment Info */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Shipping Address */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              {t('checkout.shippingAddress')}
            </h3>
            <div className="text-sm text-gray-600 space-y-1">
              <p className="font-medium text-gray-900">{order.shippingAddress.fullName}</p>
              <p>{order.shippingAddress.addressLine1}</p>
              {order.shippingAddress.addressLine2 && (
                <p>{order.shippingAddress.addressLine2}</p>
              )}
              <p>
                {order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.zipCode}
              </p>
              <p>{order.shippingAddress.country}</p>
              <p className="pt-2">{t('checkout.phone')}: {order.shippingAddress.phone}</p>
            </div>
          </div>

          {/* Payment Method */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              {t('checkout.paymentMethod')}
            </h3>
            <div className="text-sm text-gray-600 space-y-1">
              <p className="font-medium text-gray-900 capitalize">{order.paymentMethod}</p>
              <div className="inline-flex items-center gap-2 bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs font-semibold mt-2">
                <CheckCircle className="w-4 h-4" />
                {t('orderConfirm.paymentStatus', { status: t(`accountOrders.paymentStatus.${order.paymentStatus}`, { defaultValue: order.paymentStatus }) })}
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/shop">
            <Button className="bg-[#be1f1a] hover:bg-[#a71b17] px-8">
              {t('checkout.continueShopping')}
            </Button>
          </Link>
          <Link to="/account/orders">
            <Button variant="outline" className="px-8">
              {t('orderConfirm.viewAllOrders')}
            </Button>
          </Link>
        </div>

        {/* Email Confirmation Note */}
        <div className="text-center mt-8">
          <p className="text-sm text-gray-500">
            {t('orderConfirm.emailSentTo')} <span className="font-medium">{order.customerEmail}</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default OrderConfirmationPage;